<template>
  <div>
    <!-- Call Queue Sub-Tabs -->
    <div v-if="userCallQueues.length > 0" class="bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-2 mb-3">
      <div class="flex flex-wrap items-center gap-1.5">
        <!-- Persönlich + Anrufweiterleitung -->
        <div class="flex items-center gap-0.5">
          <button
            @click="changeCallQueue('personal')"
            class="px-3 py-1.5 text-sm font-medium rounded-lg transition-all"
            :class="selectedCallQueue === 'personal'
              ? 'bg-slate-700 dark:bg-slate-200 text-white dark:text-slate-900 shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'"
          >
            👤 Persönlich
          </button>
        </div>

        <!-- Call Queues + An-/Abmelden jeweils -->
        <div
          v-for="queue in userCallQueues"
          :key="queue.queue_email"
          class="flex items-center gap-0.5"
        >
          <button
            @click="changeCallQueue(queue.queue_email)"
            class="px-3 py-1.5 text-sm font-medium rounded-lg transition-all"
            :class="selectedCallQueue === queue.queue_email
              ? 'bg-slate-700 dark:bg-slate-200 text-white dark:text-slate-900 shadow-sm'
              : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'"
          >
            📞 {{ getQueueDisplayName(queue.queue_name) }}
          </button>
          <button
            @click="openCallQueueSettings"
            class="p-1.5 text-violet-500 hover:bg-violet-100 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
            title="Call Queue An-/Abmelden"
          >
            <Headphones class="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>

    <!-- Calls Filter -->
    <div class="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-3 sm:p-4 mb-4">
      <div class="flex flex-col sm:flex-row flex-wrap gap-3">
        <!-- Call Type Filter -->
        <div class="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
          <button
            @click="changeCallFilter('all')"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="callFilter === 'all' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            Alle
          </button>
          <button
            @click="changeCallFilter('missed')"
            class="px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5"
            :class="callFilter === 'missed' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            <span class="w-2 h-2 bg-red-500 rounded-full"></span>
            Verpasst
          </button>
          <button
            @click="changeCallFilter('incoming')"
            class="px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5"
            :class="callFilter === 'incoming' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            <PhoneIncoming class="w-3.5 h-3.5" />
            Eingehend
          </button>
          <button
            @click="changeCallFilter('outgoing')"
            class="px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-1.5"
            :class="callFilter === 'outgoing' 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            <PhoneOutgoing class="w-3.5 h-3.5" />
            Ausgehend
          </button>
        </div>

        <!-- Days Filter -->
        <div class="flex items-center gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
          <button
            @click="changeCallDays(1)"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="callDays === 1 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            Heute
          </button>
          <button
            @click="changeCallDays(7)"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="callDays === 7 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            7 Tage
          </button>
          <button
            @click="changeCallDays(30)"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="callDays === 30 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            30 Tage
          </button>
          <button
            @click="changeCallDays(90)"
            class="px-3 py-1.5 text-sm rounded-md transition-colors"
            :class="callDays === 90 
              ? 'bg-white dark:bg-slate-600 text-slate-800 dark:text-white shadow-sm' 
              : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'"
          >
            90 Tage
          </button>
        </div>

        <!-- Call Search -->
        <div class="flex-1 min-w-0 sm:min-w-48">
          <div class="relative group">
            <Search class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 group-focus-within:text-slate-600 dark:group-focus-within:text-slate-300 transition-colors" />
            <input
              v-model="callSearch"
              type="text"
              placeholder="Suche: Name, Telefon, Firma..."
              class="w-full pl-8 pr-8 py-1.5 bg-slate-50 dark:bg-slate-900/50 border border-slate-200/50 dark:border-slate-600/50 rounded-lg focus:border-slate-400 focus:ring-1 focus:ring-slate-500/20 text-slate-800 dark:text-slate-100 text-sm placeholder-slate-400 transition-all"
              @input="debouncedCallSearch"
            >
            <button 
              v-if="callSearch"
              @click="callSearch = ''; fetchCallHistoryWithQueueFilter()"
              class="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-red-500 transition-colors"
            >
              <X class="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
      
      <!-- Second row: Note Status + Agent Filter (only for queue view) -->
      <div v-if="isQueueSelected" class="flex flex-col sm:flex-row flex-wrap gap-3 mt-3 pt-3 border-t border-slate-200/50 dark:border-slate-700/50">
        <!-- Note Status Filter -->
        <div class="flex items-center gap-1 flex-wrap">
          <span class="text-xs text-slate-500 dark:text-slate-400 mr-1">Status:</span>
          <button
            @click="changeNoteStatusFilter('unprocessed')"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :class="callNoteStatusFilter === 'unprocessed'
              ? 'bg-slate-700 dark:bg-slate-200 text-white dark:text-slate-900 shadow-sm'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'"
          >
            Unbearbeitet
          </button>
          <button
            @click="changeNoteStatusFilter('open')"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :class="callNoteStatusFilter === 'open'
              ? 'bg-slate-700 dark:bg-slate-200 text-white dark:text-slate-900 shadow-sm'
              : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'"
          >
            Offen
          </button>
          <button
            @click="changeNoteStatusFilter('in_progress')"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :class="callNoteStatusFilter === 'in_progress'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50'"
          >
            In Bearbeitung
          </button>
          <button
            @click="changeNoteStatusFilter('completed')"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :class="callNoteStatusFilter === 'completed'
              ? 'bg-green-600 text-white shadow-sm'
              : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50'"
          >
            Erledigt
          </button>
          <button
            @click="changeNoteStatusFilter('forwarded')"
            class="px-2 py-1 text-xs rounded-md transition-colors"
            :class="callNoteStatusFilter === 'forwarded'
              ? 'bg-purple-600 text-white shadow-sm'
              : 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/50'"
          >
            Weitergeleitet
          </button>
        </div>

        <!-- Agent Filter -->
        <div v-if="queueMembers.length > 0" class="flex items-center gap-1">
          <span class="text-xs text-slate-500 dark:text-slate-400 mr-1">Agent:</span>
          <select
            v-model="callAgentFilter"
            @change="changeAgentFilter(callAgentFilter)"
            class="appearance-none pl-2 pr-6 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-xs text-slate-700 dark:text-slate-200 cursor-pointer"
          >
            <option value="">Alle Agents</option>
            <option 
              v-for="member in queueMembers" 
              :key="member.member_user_id" 
              :value="member.member_user_id"
            >
              {{ member.member_name }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Statistics Toggle Button -->
    <div class="mb-3">
      <button
        @click="showCallStats = !showCallStats"
        class="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl transition-colors w-full sm:w-auto"
        :class="showCallStats
          ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 shadow-md'
          : 'bg-white/90 dark:bg-slate-800/90 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-200/50 dark:border-slate-700/50'"
      >
        <BarChart3 class="w-4 h-4" />
        <span>Statistiken</span>
        <ChevronDown v-if="!showCallStats" class="w-4 h-4 ml-auto sm:ml-1 transition-transform" />
        <ChevronUp v-else class="w-4 h-4 ml-auto sm:ml-1 transition-transform" />
      </button>
    </div>

    <!-- Collapsible Statistics Container -->
    <div
      class="overflow-hidden transition-all duration-300 ease-in-out"
      :style="{ maxHeight: showCallStats ? '2000px' : '0px', opacity: showCallStats ? 1 : 0 }"
    >

    <!-- Call Queue Statistics Panel -->
    <div v-if="isQueueSelected && queueStats" class="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200/50 dark:border-purple-700/30 rounded-2xl p-4 mb-4">
      <div class="flex flex-col lg:flex-row gap-4">
        <!-- Summary Stats -->
        <div class="flex-1 grid grid-cols-3 gap-3">
          <div class="bg-white/80 dark:bg-slate-800/80 rounded-xl p-3 text-center">
            <div class="text-2xl font-bold text-slate-800 dark:text-white">{{ queueStats.summary?.total_calls || 0 }}</div>
            <div class="text-xs text-slate-500 dark:text-slate-400">Gesamt</div>
          </div>
          <div class="bg-white/80 dark:bg-slate-800/80 rounded-xl p-3 text-center">
            <div class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{{ queueStats.summary?.answered_calls || 0 }}</div>
            <div class="text-xs text-slate-500 dark:text-slate-400">Angenommen</div>
          </div>
          <div class="bg-white/80 dark:bg-slate-800/80 rounded-xl p-3 text-center">
            <div class="text-2xl font-bold text-red-500 dark:text-red-400">{{ queueStats.summary?.missed_calls || 0 }}</div>
            <div class="text-xs text-slate-500 dark:text-slate-400">Verpasst</div>
          </div>
        </div>

        <!-- Answer Rate Circle -->
        <div class="flex items-center justify-center">
          <div class="relative w-20 h-20">
            <svg class="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="16" fill="none" class="stroke-current text-slate-200 dark:text-slate-700" stroke-width="3"/>
              <circle 
                cx="18" cy="18" r="16" fill="none" 
                class="stroke-current text-emerald-500" 
                stroke-width="3"
                stroke-linecap="round"
                :stroke-dasharray="`${(queueStats.summary?.answer_rate || 0)} 100`"
              />
            </svg>
            <div class="absolute inset-0 flex items-center justify-center">
              <span class="text-lg font-bold text-slate-800 dark:text-white">{{ queueStats.summary?.answer_rate || 0 }}%</span>
            </div>
          </div>
        </div>

        <!-- Agent Stats (top 3) -->
        <div class="flex-1 space-y-2">
          <div class="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Top Agenten</div>
          <div 
            v-for="agent in (queueStats.agents || []).slice(0, 3)" 
            :key="agent.user_id"
            class="flex items-center gap-2"
          >
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{{ agent.user_name }}</div>
            </div>
            <div class="flex items-center gap-2">
              <div class="w-16 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5">
                <div 
                  class="bg-emerald-500 h-1.5 rounded-full" 
                  :style="{ width: `${agent.percentage || 0}%` }"
                ></div>
              </div>
              <span class="text-xs font-medium text-slate-600 dark:text-slate-400 w-8 text-right">{{ agent.calls_answered }}</span>
            </div>
          </div>
          <div v-if="!queueStats.agents?.length" class="text-xs text-slate-400 italic">Keine beantworteten Anrufe</div>
        </div>
      </div>
    </div>

    <!-- Task/Note Status Statistics Panel -->
    <div v-if="isQueueSelected && noteStats" class="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 border border-amber-200/50 dark:border-amber-700/30 rounded-2xl p-3 mb-4">
      <div class="flex items-center gap-2 mb-2">
        <svg class="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
        <span class="text-xs font-semibold text-amber-700 dark:text-amber-300">Aufgaben-Status</span>
      </div>
      <div class="grid grid-cols-3 sm:grid-cols-5 gap-2">
        <button 
          @click="changeNoteStatusFilter('unprocessed')"
          :class="[
            'rounded-lg p-2 text-center transition-colors cursor-pointer',
            callNoteStatusFilter === 'unprocessed' ? 'ring-2 ring-slate-500' : ''
          ]"
          class="bg-white/80 dark:bg-slate-800/80"
        >
          <div class="text-lg font-bold text-slate-600 dark:text-slate-300">{{ noteStats.unprocessed_count || 0 }}</div>
          <div class="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">Unbearbeitet</div>
        </button>
        <button 
          @click="changeNoteStatusFilter('open')"
          :class="[
            'rounded-lg p-2 text-center transition-colors cursor-pointer',
            callNoteStatusFilter === 'open' ? 'ring-2 ring-slate-500' : ''
          ]"
          class="bg-white/80 dark:bg-slate-800/80"
        >
          <div class="text-lg font-bold text-slate-600 dark:text-slate-300">{{ noteStats.open_count || 0 }}</div>
          <div class="text-[10px] text-slate-500 dark:text-slate-400 leading-tight">Offen</div>
        </button>
        <button 
          @click="changeNoteStatusFilter('in_progress')"
          :class="[
            'rounded-lg p-2 text-center transition-colors cursor-pointer',
            callNoteStatusFilter === 'in_progress' ? 'ring-2 ring-blue-500' : ''
          ]"
          class="bg-blue-50/80 dark:bg-blue-900/20"
        >
          <div class="text-lg font-bold text-blue-600 dark:text-blue-400">{{ noteStats.in_progress_count || 0 }}</div>
          <div class="text-[10px] text-blue-500 dark:text-blue-400 leading-tight">In Bearbeitung</div>
        </button>
        <button 
          @click="changeNoteStatusFilter('completed')"
          :class="[
            'rounded-lg p-2 text-center transition-colors cursor-pointer',
            callNoteStatusFilter === 'completed' ? 'ring-2 ring-green-500' : ''
          ]"
          class="bg-green-50/80 dark:bg-green-900/20"
        >
          <div class="text-lg font-bold text-green-600 dark:text-green-400">{{ noteStats.completed_count || 0 }}</div>
          <div class="text-[10px] text-green-500 dark:text-green-400 leading-tight">Erledigt</div>
        </button>
        <button 
          @click="changeNoteStatusFilter('forwarded')"
          :class="[
            'rounded-lg p-2 text-center transition-colors cursor-pointer',
            callNoteStatusFilter === 'forwarded' ? 'ring-2 ring-purple-500' : ''
          ]"
          class="bg-purple-50/80 dark:bg-purple-900/20"
        >
          <div class="text-lg font-bold text-purple-600 dark:text-purple-400">{{ noteStats.forwarded_count || 0 }}</div>
          <div class="text-[10px] text-purple-500 dark:text-purple-400 leading-tight">Weitergeleitet</div>
        </button>
      </div>
    </div>

    <!-- Call Analytics -->
    <CallAnalytics
      :mode="isQueueSelected ? 'queue' : 'personal'"
      :queueEmail="isQueueSelected ? selectedCallQueue : ''"
      :queueName="isQueueSelected ? getSelectedQueueName() : ''"
      :days="callDays"
      class="mb-4"
    />

    </div><!-- End Collapsible Statistics Container -->

    <!-- Calls Count + Export -->
    <div class="flex items-center justify-between mb-3 text-sm px-1">
      <div class="flex items-center gap-2 text-slate-600 dark:text-slate-400">
        <span class="font-medium text-slate-800 dark:text-slate-200">{{ callRecords.length }}</span>
        <span v-if="callRecordsPagination.total > callRecords.length" class="text-slate-400">von {{ callRecordsPagination.total }}</span>
        <span>Anrufe</span>
        <span v-if="callRecordsLoading" class="flex items-center gap-1.5 text-blue-600">
          <Loader2 class="w-3.5 h-3.5 animate-spin" />
          <span class="text-xs">Laden...</span>
        </span>
      </div>
      <div class="flex items-center gap-2">
        <!-- Export CSV -->
        <button
          @click="exportCallsCSV"
          :disabled="isExportingCalls"
          class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
          title="Anrufe als CSV exportieren"
        >
          <Loader2 v-if="isExportingCalls" class="w-3.5 h-3.5 animate-spin" />
          <Download v-else class="w-3.5 h-3.5" />
          <span class="hidden sm:inline">Export</span>
        </button>
        <span v-if="callRecordsPagination.hasMore" class="text-slate-400 text-xs hidden sm:inline">Scrollen für mehr ↓</span>
      </div>
    </div>

    <!-- Call Records Layout: Calls + Optional CQ Member Sidebar -->
    <div class="flex gap-4">
      <div class="flex-1 min-w-0 space-y-1.5">
        <CallCard
          v-for="call in callRecords"
          :key="call.id"
          :call="call"
          :currentUserId="currentUserId"
          :hasNote="!!call.note_status"
          :isQueueView="isQueueSelected"
          @open-contact="openContactByEmail"
          @open-notes="openCallNotes"
        />
      
        <!-- Infinite Scroll Trigger for Calls -->
        <div ref="callsScrollTrigger" class="h-16 flex items-center justify-center mt-4">
          <div v-if="callRecordsLoading && callRecords.length > 0" class="flex items-center gap-2 text-slate-500 text-sm">
            <Loader2 class="w-4 h-4 animate-spin" />
            <span>Laden...</span>
          </div>
          <div v-else-if="!callRecordsPagination.hasMore && callRecords.length > 0" class="text-slate-400 text-sm">
            Alle {{ callRecordsPagination.total }} Anrufe geladen
          </div>
        </div>

        <!-- Empty State for Calls -->
        <div v-if="callRecords.length === 0 && !callRecordsLoading" class="text-center py-12">
          <Phone class="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p class="text-slate-500 dark:text-slate-400 mb-1">
            {{ callFilter === 'missed' ? 'Keine verpassten Anrufe' : 'Keine Anrufe gefunden' }}
          </p>
          <p class="text-slate-400 text-xs">
            Anrufe werden automatisch erfasst, sobald sie stattfinden
          </p>
        </div>

        <!-- Loading State for Calls -->
        <div v-if="callRecords.length === 0 && callRecordsLoading" class="text-center py-12">
          <Loader2 class="w-8 h-8 mx-auto mb-3 text-blue-500 animate-spin" />
          <p class="text-slate-500 text-sm">Lade Anrufe...</p>
        </div>
      </div>

      <!-- CQ Members Sidebar (only when a queue is selected) -->
      <div 
        v-if="isQueueSelected && queueMembers.length > 0" 
        class="hidden lg:block w-56 flex-shrink-0"
      >
        <div class="sticky top-4 bg-white/90 dark:bg-slate-800/90 border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-4 shadow-sm">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            <Users class="w-4 h-4" />
            Mitglieder
            <span class="text-xs text-slate-400 font-normal">({{ queueMembers.length }})</span>
          </h3>
          <div class="space-y-2">
            <div 
              v-for="member in queueMembers" 
              :key="member.member_user_id"
              @click="openContactByEmail(member.member_email)"
              class="flex items-center gap-2.5 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded-lg p-1.5 -mx-1.5 transition-colors"
            >
              <!-- Presence dot -->
              <div 
                class="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                :class="getPresenceClass(member.member_user_id)"
              ></div>
              <!-- Name + Status -->
              <div class="flex-1 min-w-0">
                <div class="text-sm text-slate-700 dark:text-slate-300 truncate">{{ member.member_name }}</div>
                <div class="text-[10px] text-slate-400 dark:text-slate-500">{{ getPresenceLabel(member.member_user_id) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { Search, X, Users, Loader2, ChevronDown, ChevronUp, Phone, PhoneIncoming, PhoneOutgoing, Download, BarChart3, Headphones } from 'lucide-vue-next';
import { 
  contacts,
  callRecords, callRecordsLoading, callRecordsPagination,
  fetchCallHistory, loadMoreCallRecords,
  userCallQueues, selectedCallQueue, fetchUserCallQueues,
  getPresenceClass, getPresenceLabel
} from '../websocketService';
import { authenticatedFetch } from '../oauth2-config';
import CallCard from './CallCard.vue';
import CallAnalytics from './CallAnalytics.vue';

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const emit = defineEmits(['open-call-notes', 'select-contact']);

// Call filter state
const callFilter = ref('all');
const callDays = ref(7);
const currentUserId = ref(localStorage.getItem('user_id') || '');
const callSearch = ref('');
const callNoteStatusFilter = ref('');
const callAgentFilter = ref('');
const isExportingCalls = ref(false);
const callsScrollTrigger = ref(null);

// Call Statistics visibility toggle (collapsed by default)
const showCallStats = ref(false);

// Call Queue Statistics state
const queueStats = ref(null);
const queueStatsLoading = ref(false);

// Note/Task Statistics state
const noteStats = ref(null);
const noteStatsLoading = ref(false);

// Call Queue Members state (for sidebar)
const queueMembers = ref([]);
const queueMembersLoading = ref(false);

// Export error
const exportError = ref(null);

// Computed: Check if a queue is selected (not 'personal' and not 'all')
const isQueueSelected = computed(() => {
  return selectedCallQueue.value && selectedCallQueue.value !== 'personal' && selectedCallQueue.value !== 'all';
});

let callSearchTimeout = null;
let callsObserver = null;

// Helper to fetch call history with current queue filter
const fetchCallHistoryWithQueueFilter = () => {
  const options = { filter: callFilter.value, days: callDays.value };
  if (userCallQueues.value.length > 0) {
    if (selectedCallQueue.value === 'personal') {
      options.personal = true;
    } else if (selectedCallQueue.value && selectedCallQueue.value !== 'all') {
      options.queueEmail = selectedCallQueue.value;
    }
  }
  if (callSearch.value.trim()) {
    options.search = callSearch.value.trim();
  }
  if (callNoteStatusFilter.value) {
    options.noteStatus = callNoteStatusFilter.value;
  }
  if (callAgentFilter.value) {
    options.answeredById = callAgentFilter.value;
  }
  return fetchCallHistory(options, true);
};

// Change call queue filter
const changeCallQueue = async (queueEmail) => {
  selectedCallQueue.value = queueEmail;
  queueStats.value = null;
  noteStats.value = null;
  queueMembers.value = [];
  callSearch.value = '';
  callNoteStatusFilter.value = '';
  callAgentFilter.value = '';
  await fetchCallHistoryWithQueueFilter();
  if (queueEmail && queueEmail !== 'personal' && queueEmail !== 'all') {
    fetchQueueStats(queueEmail);
    fetchQueueMembers(queueEmail);
    fetchNoteStats(queueEmail);
  }
};

// Fetch queue statistics
const fetchQueueStats = async (queueEmail) => {
  if (!queueEmail) return;
  try {
    queueStatsLoading.value = true;
    const response = await authenticatedFetch(`/api/calls/queues/${encodeURIComponent(queueEmail)}/stats?days=${callDays.value}`);
    if (response.ok) {
      queueStats.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching queue stats:', error);
  } finally {
    queueStatsLoading.value = false;
  }
};

// Fetch queue members (for sidebar)
const fetchQueueMembers = async (queueEmail) => {
  if (!queueEmail) return;
  try {
    queueMembersLoading.value = true;
    const response = await authenticatedFetch(`/api/calls/queues/${encodeURIComponent(queueEmail)}/members`);
    if (response.ok) {
      queueMembers.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching queue members:', error);
  } finally {
    queueMembersLoading.value = false;
  }
};

// Get display name for Call Queue (shorten if needed)
const getQueueDisplayName = (name) => {
  if (!name) return 'Call Queue';
  let displayName = name.replace(/\s+(AA|CQ)$/i, '');
  if (displayName.length > 25) {
    return displayName.substring(0, 22) + '...';
  }
  return displayName;
};

// Quick actions: open external Teams settings pages
const openCallQueueSettings = () => {
  window.open('https://dialin.teams.microsoft.com/usp/callqueues', '_blank', 'noopener,noreferrer');
};

// Get the currently selected queue's display name (for analytics)
const getSelectedQueueName = () => {
  if (!isQueueSelected.value) return '';
  const queue = userCallQueues.value.find(q => q.queue_email === selectedCallQueue.value);
  return queue ? getQueueDisplayName(queue.queue_name) : 'Call Queue';
};

// Change call filter
const changeCallFilter = (filter) => {
  callFilter.value = filter;
  fetchCallHistoryWithQueueFilter();
};

// Change call days filter
const changeCallDays = async (days) => {
  callDays.value = days;
  await fetchCallHistoryWithQueueFilter();
  if (isQueueSelected.value) {
    fetchQueueStats(selectedCallQueue.value);
    fetchNoteStats(selectedCallQueue.value);
  }
};

// Debounced search for calls
const debouncedCallSearch = () => {
  clearTimeout(callSearchTimeout);
  callSearchTimeout = window.setTimeout(() => {
    fetchCallHistoryWithQueueFilter();
  }, 400);
};

// Change note status filter
const changeNoteStatusFilter = (status) => {
  callNoteStatusFilter.value = callNoteStatusFilter.value === status ? '' : status;
  fetchCallHistoryWithQueueFilter();
};

// Change agent filter
const changeAgentFilter = (agentId) => {
  callAgentFilter.value = callAgentFilter.value === agentId ? '' : agentId;
  fetchCallHistoryWithQueueFilter();
};

// Fetch note/task statistics for a queue
const fetchNoteStats = async (queueEmail) => {
  if (!queueEmail) return;
  try {
    noteStatsLoading.value = true;
    const response = await authenticatedFetch(`/api/calls/queues/${encodeURIComponent(queueEmail)}/note-stats?days=${callDays.value}`);
    if (response.ok) {
      noteStats.value = await response.json();
    }
  } catch (error) {
    console.error('Error fetching note stats:', error);
  } finally {
    noteStatsLoading.value = false;
  }
};

// Open contact details by email (used when clicking on a call or CQ member)
const openContactByEmail = async (email) => {
  if (!email) return;
  
  let contact = contacts.value.find(c => c.email?.toLowerCase() === email.toLowerCase());
  
  if (!contact) {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/contacts/by-email/${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        if (data) {
          contact = data;
        }
      }
    } catch (error) {
      console.error('Could not find contact:', error);
    }
  }
  
  if (contact) {
    emit('select-contact', contact);
  }
};

// Open call notes modal
const openCallNotes = (call) => {
  emit('open-call-notes', call);
};

// Handle when a note is saved (called from parent via exposed method)
const handleNoteSaved = (note) => {
  const callIndex = callRecords.value.findIndex(c => c.id === note.call_id);
  if (callIndex !== -1) {
    callRecords.value[callIndex].note_status = note.status;
    callRecords.value[callIndex].note_assigned_to = note.assigned_to_name;
    callRecords.value[callIndex].note_assigned_to_id = note.assigned_to_id;
    callRecords.value[callIndex].note_assigned_to_name = note.assigned_to_name;
  }
  if (isQueueSelected.value) {
    fetchNoteStats(selectedCallQueue.value);
  }
};

// Export call history to CSV
const exportCallsCSV = () => {
  if (isExportingCalls.value || callRecords.value.length === 0) return;
  
  isExportingCalls.value = true;
  try {
    const headers = ['Datum', 'Uhrzeit', 'Typ', 'Richtung', 'Anrufer/Organisator', 'Firma', 'Externe Nummer', 'Beantwortet von', 'Dauer (Sek)', 'Verpasst', 'Call Queue'];
    const rows = callRecords.value.map(call => {
      const startDate = call.start_time ? new Date(call.start_time) : null;
      const datum = startDate ? startDate.toLocaleDateString('de-DE') : '';
      const uhrzeit = startDate ? startDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';
      const typ = call.call_type === 'groupCall' ? 'Gruppenanruf' : 'Direktanruf';
      const richtung = call.direction === 'inbound' ? 'Eingehend' : call.direction === 'outbound' ? 'Ausgehend' : '';
      const anrufer = call.organizer_name || call.organizer_email || call.organizer_phone || '';
      const firma = call.external_company_name || '';
      const externeNummer = call.external_phone || call.organizer_phone || '';
      const beantwortet = call.answered_by_name || '';
      const dauer = call.duration_seconds || 0;
      const verpasst = call.participants?.some(p => p.is_missed) ? 'Ja' : 'Nein';
      const queue = call.queue_name || '';
      return [datum, uhrzeit, typ, richtung, anrufer, firma, externeNummer, beantwortet, dauer, verpasst, queue];
    });

    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const dateStr = new Date().toISOString().slice(0, 10);
    link.download = `anrufe_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Call export failed:', error);
    exportError.value = 'Export fehlgeschlagen';
    setTimeout(() => { exportError.value = null; }, 5000);
  } finally {
    isExportingCalls.value = false;
  }
};

// Initialize calls tab on mount
const initCallsTab = async () => {
  // Clear old call records and reset pagination
  callRecords.value = [];
  callRecordsPagination.value = { total: 0, limit: 50, offset: 0, hasMore: false };
  // Fetch user's Call Queue memberships
  await fetchUserCallQueues();
  // Reset queue filter
  if (userCallQueues.value.length > 0) {
    selectedCallQueue.value = 'personal';
  } else {
    selectedCallQueue.value = 'all';
  }
  await fetchCallHistoryWithQueueFilter();
};

// Expose methods for parent component
defineExpose({ handleNoteSaved, initCallsTab });

onMounted(() => {
  // Initialize calls data only on first mount (not when tab is re-shown via v-show)
  if (callRecords.value.length === 0) {
    initCallsTab();
  }

  // Observer for calls infinite scroll
  callsObserver = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && callRecordsPagination.value.hasMore && !callRecordsLoading.value) {
        loadMoreCallRecords();
      }
    },
    { threshold: 0.1, rootMargin: '100px' }
  );

  if (callsScrollTrigger.value) {
    callsObserver.observe(callsScrollTrigger.value);
  }
});

onUnmounted(() => {
  if (callsObserver) {
    callsObserver.disconnect();
  }
  clearTimeout(callSearchTimeout);
});

// Watch for callsScrollTrigger ref changes
watch(callsScrollTrigger, (newEl, oldEl) => {
  if (oldEl && callsObserver) callsObserver.unobserve(oldEl);
  if (newEl && callsObserver) callsObserver.observe(newEl);
});
</script>
